import React, { useEffect, useMemo, useState } from 'react';

import { SmButton } from '@smiles/smiles-react-design-system';
import Mustache from 'mustache';

import useApi from '@/hooks/useApi';
import useEvent from '@/hooks/useEvent';

import { TModalContent } from '@/services/requests/Liferay/types';
import { dispatchInteraction } from '@/services/requests/Interactions/helpers';
import { TVoucherItem } from '@/services/requests/Voucher/types';
import { TVoucherPurchaseInput } from '@/services/requests/checkout/types';
import { submitVoucherPurchase } from '@/services/requests/checkout/service';

import { clearHtmlTags } from '@/helpers/HtmlHelper/clearHtmlTags';

import { TScreens } from '../..';
import { ScreenLoading } from '../Loading';

type TProps = {
  onBack: () => void;
  onClose: () => void;
  liferayContent: TModalContent;
  totalSelected: number;
  voucherLimit: number;
  milesValueTotal: string;
  totalDiscount: string;
  totalToPay: string;
  changeScreen: (newScreen: TScreens) => void;
  currentVoucher: TVoucherItem | undefined;
  purchaseId: string;
  voucherListLength: number;
};

export const FinishOrder = ({
  onBack,
  onClose,
  liferayContent,
  voucherLimit,
  totalSelected,
  milesValueTotal,
  totalDiscount,
  totalToPay,
  changeScreen,
  currentVoucher,
  purchaseId,
  voucherListLength,
}: TProps) => {
  useEffect(() => {
    if (!currentVoucher) {
      changeScreen('error');
    }
  }, [currentVoucher, changeScreen]);

  const liferayFilled = useMemo<TModalContent>(() => {
    return JSON.parse(
      Mustache.render(JSON.stringify(liferayContent), currentVoucher),
    );
  }, [liferayContent, currentVoucher]);

  const {
    title,
    buyingLabel,
    milesLabel,
    voucherLimitLabel,
    discountPercLabel,
    milesTotalLabel,
    discountLabel,
    totalLabel,
    chooseVoucherLink,
    ruleBtn,
    buyBtn,
  } = liferayFilled;

  const { ApiInteractions, ApiCheckout } = useApi();
  const event = useEvent();

  const [isLoading, setLoading] = useState<boolean>(false);

  const handleFinishOrder = async () => {
    setLoading(true);
    const params = {
      milesQuantity: totalSelected,
      optionId: purchaseId,
      voucherNumber: currentVoucher?.voucherNumber,
    } as TVoucherPurchaseInput;

    dispatchInteraction(ApiInteractions, 'extra_mpv_summary_click_checkout', {
      recommendationId: event.data.recommendationId,
    });
    const resp = await submitVoucherPurchase(ApiCheckout, params);
    if (resp === 'success') {
      window.location.href = '/group/guest/checkout';
    } else {
      setLoading(false);
      changeScreen('error');
    }
  };

  const handleRules = () => {
    dispatchInteraction(ApiInteractions, 'extra_mpv_summary_click_rules', {
      recommendationId: event.data.recommendationId,
    });

    changeScreen('rules');
  };

  const handleBack = (): void => {
    dispatchInteraction(ApiInteractions, 'extra_mpv_summary_click_back', {
      recommendationId: event.data.recommendationId,
    });
    onBack();
  };

  const handlechooseVoucher: React.MouseEventHandler<HTMLAnchorElement> = (
    e,
  ): void => {
    e.preventDefault();
    dispatchInteraction(
      ApiInteractions,
      'extra_mpv_summary_click_other_voucher',
      {
        recommendationId: event.data.recommendationId,
      },
    );

    changeScreen('voucherlist');
  };

  const percent = (totalSelected * 100) / voucherLimit;

  return (
    <>
      {isLoading ? (
        <ScreenLoading onClose={onClose} />
      ) : (
        <div className="smls-b2c-modals__modal-content">
          <div className="smls-b2c-modals__button--back">
            <SmButton
              onClick={handleBack}
              isPill
              isOutlined
              icon={<span className="material-icons">arrow_back_ios</span>}
            />
          </div>
          <div className="finish-order">
            {title?.text && (
              <div
                className="finish-order__title" // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: title.text }}
              />
            )}

            <div className="finish-order__card">
              {buyingLabel?.text && (
                <span
                  className="finish-order__card__title" // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: buyingLabel.text,
                  }}
                />
              )}
              <div className="finish-order__card__miles-qty">
                <div className="icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.8 10.2C17.9 9.7 18 9.1 18 8.5C18 4.9 15.1 2 11.5 2C7.9 2 5 4.9 5 8.5C5 9.1 5.1 9.7 5.2 10.2C5.1 10.8 5 11.4 5 12C5 12.6 5.1 13.2 5.2 13.7C5.1 14.3 5 14.9 5 15.5C5 19.1 7.9 22 11.5 22C15.1 22 18 19.1 18 15.5C18 14.9 17.9 14.3 17.8 13.7C18 13.1 18 12.6 18 12C18 11.4 17.9 10.8 17.8 10.2ZM11.5 3.8C14.1 3.8 16.2 5.9 16.2 8.5C16.2 11.1 14.1 13.2 11.5 13.2C8.9 13.2 6.8 11.1 6.8 8.5C6.8 5.9 8.9 3.8 11.5 3.8ZM16 13.1C15.5 15.1 13.7 16.7 11.5 16.7C9.3 16.7 7.5 15.2 7 13.1C8.1 14.3 9.7 15 11.5 15C13.3 15 14.9 14.3 16 13.1ZM11.5 20.2C9.3 20.2 7.5 18.7 7 16.6C8.2 17.7 9.8 18.5 11.5 18.5C13.3 18.5 14.9 17.8 16 16.6C15.5 18.7 13.7 20.2 11.5 20.2Z"
                      fill="#FF7020"
                    />
                  </svg>
                </div>
                <div className="value">
                  {totalSelected
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                </div>
                {milesLabel?.text && (
                  <div
                    className="suffix" // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: milesLabel.text,
                    }}
                  />
                )}
              </div>
              <div className="finish-order__card__progress-bar">
                <div className="filled" style={{ width: `${percent}%` }} />
              </div>

              <div className="finish-order__card__limit">
                {voucherLimitLabel?.text && (
                  <span
                    className="limit-label" // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: voucherLimitLabel.text }}
                  />
                )}
                {milesLabel?.text && (
                  <span
                    className="label" // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: `${voucherLimit
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} ${clearHtmlTags(
                        milesLabel.text,
                      )}`,
                    }}
                  />
                )}
              </div>

              <div className="finish-order__card__bonus">
                <div className="icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20.7 12.6L11.4 3.3C11.3 3.1 11 3 10.7 3H4C3.4 3 3 3.4 3 4V10.7C3 11 3.1 11.3 3.3 11.4L12.6 20.7C12.8 20.9 13 21 13.3 21C13.6 21 13.8 20.9 14 20.7L20.7 14C20.9 13.8 21 13.6 21 13.3C21 13 20.9 12.7 20.7 12.6ZM13.3 18.6L5 10.3V5H10.3L18.6 13.3L13.3 18.6Z"
                      fill="#478331"
                    />
                    <path
                      d="M7.30078 8.2998C7.85307 8.2998 8.30078 7.85209 8.30078 7.2998C8.30078 6.74752 7.85307 6.2998 7.30078 6.2998C6.7485 6.2998 6.30078 6.74752 6.30078 7.2998C6.30078 7.85209 6.7485 8.2998 7.30078 8.2998Z"
                      fill="#478331"
                    />
                  </svg>
                </div>
                {discountPercLabel?.text && (
                  <div
                    className="text" // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: discountPercLabel.text }}
                  />
                )}
              </div>
            </div>

            <div className="finish-order__total-table">
              <div className="finish-order__total-table__line">
                {milesTotalLabel?.text && (
                  <div
                    className="description" // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: milesTotalLabel.text }}
                  />
                )}
                <div className="symbol">R$</div>
                <div className="number">{milesValueTotal}</div>
              </div>
              <div className="finish-order__total-table__line">
                {discountLabel?.text && (
                  <div
                    className="description" // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: discountLabel.text }}
                  />
                )}
                <div className="symbol">R$</div>
                <div className="number">{totalDiscount}</div>
              </div>
              <div className="finish-order__total-table__line finish-order__total-table__line--highlighted">
                {totalLabel?.text && (
                  <div
                    className="description" // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: totalLabel.text }}
                  />
                )}
                <div className="symbol">R$</div>
                <div className="number">{totalToPay}</div>
              </div>
            </div>

            {voucherListLength > 1 && chooseVoucherLink?.text && (
              <a
                href="href"
                className="finish-order__link"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: chooseVoucherLink.text }}
                aria-label={clearHtmlTags(chooseVoucherLink.text)}
                onClick={handlechooseVoucher}
              />
            )}

            <div className="finish-order__footer">
              {ruleBtn?.text && (
                <SmButton
                  type="button"
                  text={clearHtmlTags(ruleBtn.text)}
                  isOutlined
                  onClick={handleRules}
                />
              )}
              {buyBtn?.text && (
                <SmButton
                  type="button"
                  text={clearHtmlTags(buyBtn.text)}
                  onClick={handleFinishOrder}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
